import type { ImpedanceDataDto } from '../types';

export interface DeviceJsonStructure {
  uid?: string;
  state?: {
    electrode?: string;
  };
  impedance_result?: {
    frequency_Hz: number[];
    data: Array<{
      magnitude_Ohm: number[];
      phase_rad: number[];
    }>;
  };
}

/**
 * Extract impedance data from device JSON structure.
 * `impedance_result.data[]` already holds one entry per channel with its own
 * per-point `magnitude_Ohm`/`phase_rad` arrays — `ImpedanceDataDto`'s 2D shape
 * matches this natively, so no single-channel wrapping is needed.
 */
export function extractImpedanceData(json: any): ImpedanceDataDto | null {
  try {
    if (!json?.impedance_result) {
      return null;
    }

    const result = json.impedance_result;
    const frequencies = result.frequency_Hz || [];
    const data = result.data || [];

    if (frequencies.length === 0 || data.length === 0) {
      return null;
    }

    return {
      frequency_Hz: frequencies,
      magnitude_Ohm: data.map((d: any) => d.magnitude_Ohm),
      phase_Rad: data.map((d: any) => d.phase_rad),
    };
  } catch {
    return null;
  }
}

/**
 * Extract device configuration from JSON
 */
export function extractDeviceConfig(json: any): {
  deviceId?: string;
  electrodeType?: string;
} {
  try {
    const deviceId = json?.uid;
    const electrodeType = json?.state?.electrode;

    return {
      deviceId: deviceId,
      electrodeType: electrodeType,
    };
  } catch {
    return {};
  }
}

/**
 * Parse and validate JSON structure
 */
export function parseAndValidateJson(jsonString: string): {
  data: any | null;
  error: string | null;
} {
  try {
    const data = JSON.parse(jsonString);
    return { data, error: null };
  } catch (e) {
    return {
      data: null,
      error: e instanceof Error ? e.message : 'Invalid JSON',
    };
  }
}
